
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Review } from '@/lib/types';
import ReviewCard from '@/components/review-card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import ReviewDialog from '@/components/review/ReviewDialog';

interface CompanyReviewsProps {
  companyReviews: Review[];
  companyName: string;
  currentPage: number;
  totalPages: number;
  currentReviews: Review[];
  handlePageChange: (page: number) => void;
  isLoading?: boolean;
}

const CompanyReviews = ({ 
  companyReviews, 
  companyName,
  currentPage,
  totalPages,
  currentReviews,
  handlePageChange,
  isLoading = false
}: CompanyReviewsProps) => {
  const [showMoreReviews, setShowMoreReviews] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  
  if (isLoading) {
    return (
      <div className="animate-fade-in space-y-6">
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="border rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <Skeleton className="h-6 w-1/4" />
                <Skeleton className="h-4 w-1/6" />
              </div>
              <Skeleton className="h-20 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (companyReviews.length === 0) {
    return (
      <div className="text-center py-16 animate-fade-in">
        <h3 className="text-xl font-medium text-redflags-dark mb-2">No reviews available</h3>
        <p className="text-redflags-gray mb-6">
          Be the first to leave a review for {companyName}.
        </p>
        <Button 
          asChild
          className="bg-redflags-red hover:bg-redflags-red/90 text-white"
        >
          <Link to="/add-review">Add a review</Link>
        </Button>
      </div>
    );
  }

  // Sort reviews by createdAt date (newest first)
  const sortedReviews = [...companyReviews].sort((a, b) => b.createdAt - a.createdAt);
  
  // Take only the latest 3 for initial display
  const latestReviews = sortedReviews.slice(0, 3);
  
  // Next 10 reviews for "Read more" section
  const nextReviews = sortedReviews.slice(3, 13);
  
  // Check if there are more reviews to show
  const hasMoreReviews = sortedReviews.length > 3;
  
  const handleReviewClick = (review: Review) => {
    setSelectedReview(review);
  };
  
  const handleCloseDialog = () => {
    setSelectedReview(null);
  };

  return (
    <>
      <div className="space-y-6 mb-6 animate-fade-in">
        <h3 className="text-xl font-medium text-redflags-dark mb-4">Latest Reviews</h3>
        {latestReviews.map(review => (
          <div key={review.id} onClick={() => handleReviewClick(review)} className="cursor-pointer">
            <ReviewCard 
              review={review} 
              hideCompany 
              className="w-full"
              layout="horizontal" 
            />
          </div>
        ))}
      </div>
      
      {hasMoreReviews && (
        <div className="mb-10">
          {!showMoreReviews ? (
            <div className="text-center">
              <Button 
                onClick={() => setShowMoreReviews(true)}
                variant="outline" 
                className="border-redflags-red text-redflags-red hover:bg-redflags-red/10"
              >
                Read more reviews
              </Button>
            </div>
          ) : (
            <div className="space-y-6 animate-fade-in">
              <h3 className="text-xl font-medium text-redflags-dark mt-8 mb-4">More Reviews</h3>
              {nextReviews.map(review => (
                <div key={review.id} onClick={() => handleReviewClick(review)} className="cursor-pointer">
                  <ReviewCard 
                    review={review} 
                    hideCompany 
                    className="w-full"
                    layout="horizontal" 
                  />
                </div>
              ))}
              <div className="text-center mt-6">
                <Button 
                  onClick={() => setShowMoreReviews(false)}
                  variant="outline" 
                  className="border-redflags-gray text-redflags-gray hover:bg-redflags-gray/10"
                >
                  Show less
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Review Dialog */}
      {selectedReview && (
        <ReviewDialog 
          review={selectedReview} 
          isOpen={!!selectedReview} 
          onClose={handleCloseDialog} 
        />
      )}
    </>
  );
};

export default CompanyReviews;
